import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '@src/environments/environment';
import {UserProfile} from '@app/interfaces/entities/user-profile';
import {PublicAccount, SingleResult} from '@app/interfaces';
import {ApiCachingService} from './api-caching.service';
import {tap} from 'rxjs/operators';
import {AdvertisingAccountSettings, AdvertisingDefaults, AdvertisingLocationRequirements, BusinessUnitAdvertisingSettings, CountryDescription, TranslateDescriptionPayload, TranslateDescriptionResult} from "@app/interfaces/advertising/advertising-account-settings";

export interface ChangePasswordPayload {
  current_password: string;
  password: string;
  password_confirmation: string;
}

export interface CompanionQrTokenResponse {
  success: boolean;
  keycloak_refresh_token?: string;
  expires_in?: number;
  message?: string;
  reason?: 'account_setup_incomplete' | 'generation_failed';
  required_actions?: string[];
  enabled?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserApiService {

  private isDemoBehaviourSubject = new BehaviorSubject(false);

  constructor(private http: HttpClient, private cacheApi: ApiCachingService) {
  }

  getIsDemoStream(): Observable<boolean> {
    return this.isDemoBehaviourSubject;
  }

  getUserProfile(refresh: boolean = false): Observable<SingleResult<UserProfile>> {
    if (refresh) {
      this.cacheApi.clearCached('getUserProfile');
    }

    return this.cacheApi.getCachedWithTTL(
      'getUserProfile',
      3600,
      this.http.get<SingleResult<UserProfile>>(`${environment.apiUrl}/api/users/me`)
    );
  }

  getPublicAccount(refresh: boolean = false): Observable<SingleResult<PublicAccount>> {
    if (refresh) {
      this.cacheApi.clearCached('UserApiService::getPublicAccount');
    }

    return this.cacheApi.getCachedWithTTL('UserApiService::getPublicAccount', 180, this.http.get<SingleResult<PublicAccount>>(`${environment.apiUrl}/api/public-account`));
  }

  getAccount(accountUuid: string = null): Observable<any> {
    let params = {};

    if (accountUuid) {
      params = {accountUuid};
    }

    return this.http.get(`${environment.apiUrl}/api/users/me/account`, {params});
  }

  inviteUser(email: string, firstName: string, lastName: string, extraUser?: boolean) {
    return this.http.post(`${environment.apiUrl}/api/users/invite`, {
      users: [{email, firstName, lastName}],
      extraUsers: extraUser,
    });
  }

  removeUser(email: string, sendMessage: boolean) {
    return this.http.post(`${environment.apiUrl}/api/users/remove-users`, {emails: [email], sendSlackMessage: sendMessage});
  }

  updateUserProfile(profile) {
    return this.http.put(`${environment.apiUrl}/api/users/me`, profile);
  }

  changePassword(data: ChangePasswordPayload): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.apiUrl}/api/password/change`, data);
  }

  updatePublicAccount(profile): Observable<any> {
    return this.http.put(`${environment.apiUrl}/api/public-account`, profile);
  }

  checkIfUserExists(email: string, saveAsLead = false): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/users/check-if-user-exists`, {email, saveAsLead});
  }

  getDaysTillEndOfFreePeriod(): Observable<SingleResult<any>> {
    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/public-account/days-till-end-of-trial`);
  }

  getUserProfileForm(): Observable<SingleResult<any>> {
    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/users/me/form`);
  }

  getPublicAccountForm(): Observable<SingleResult<any>> {
    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/public-account/form`);
  }

  saveContacts(contacts: any[]) {
    return this.http.put(`${environment.apiUrl}/api/public-account/contacts`, {contacts});
  }

  getAdministrationForm(): Observable<SingleResult<any>> {
    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/public-account/administration-form`);
  }

  getAdministrationAccountSettings(): Observable<SingleResult<any>> {
    return this.cacheApi.getCachedWithTTL(
      'getAdministrationAccountSettings',
      180,
      this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/public-account/administration-account-settings`)
    );
  }

  updateVehicleActivityNotifications(enabled: boolean): Observable<SingleResult<any>> {
    this.cacheApi.clearCache();
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/vehicle-activity-notifications`, {enabled});
  }

  updateLeasingVehicleDataValidation(enabled: boolean): Observable<SingleResult<any>> {
    this.cacheApi.clearCache();
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/leasing-vehicle-data-validation`, {enabled});
  }

  getUserCountryCode(): Observable<SingleResult<string>> {
    return this.cacheApi.getCachedWithTTL(
      'getUserCountryCode',
      3600,
      this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/public-account/user-country-code`));
  }

  updateAdministration(values: { [p: string]: any }) {
    this.cacheApi.clearCache();
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/administration-form`, values);
  }

  updateAdministrationBank(values: { [p: string]: any }) {
    this.cacheApi.clearCache();
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/administration-bank-form`, values);
  }

  getAdministrationKontos(): Observable<SingleResult<any>> {
    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/public-account/administration-kontos`);
  }

  saveAdministrationKontos(kontos: { [p: string]: any }) {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/administration-kontos`, {kontos});
  }

  getFiscalizationForm() {
    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/public-account/fiscalization-form`);
  }

  updateFiscalization(values: { [p: string]: any }) {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/fiscalization-form`, values);
  }

  /** Cached: the vehicle form's advertising tab re-fetches this on every mount, and
   *  until it landed the description tabs fell back to "all countries" and rendered
   *  a full row of flags that then collapsed to the real two — the flicker on every
   *  switch to Oglaševanje (BRIEFD-7404). The writers below already clear this key,
   *  which is what the caching was clearly meant to pair with. */
  getAdvertisingAccountSettings(refresh: boolean = false): Observable<SingleResult<AdvertisingAccountSettings>> {
    if (refresh) {
      this.cacheApi.clearCached('getAdvertisingAccountSettings');
    }

    return this.cacheApi.getCachedWithTTL(
      'getAdvertisingAccountSettings',
      300,
      this.http.get<SingleResult<AdvertisingAccountSettings>>(`${environment.apiUrl}/api/public-account/advertising-account-settings`),
    );
  }

  updateAdvertisingAccountSettings(settings: AdvertisingAccountSettings): Observable<SingleResult<any>> {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/advertising-account-settings`, settings)
      .pipe(
        tap(() => {
          this.cacheApi.clearCached('getAdvertisingAccountSettings');
        })
      );
  }

  translateAdvertisingDescription(payload: TranslateDescriptionPayload): Observable<TranslateDescriptionResult> {
    return this.http.post<TranslateDescriptionResult>(`${environment.apiUrl}/api/public-account/advertising-account-settings/translate`, payload);
  }

  getBusinessUnitAdvertisingSettings(businessUnitUuid: string): Observable<BusinessUnitAdvertisingSettings> {
    return this.http.get<BusinessUnitAdvertisingSettings>(`${environment.apiUrl}/api/public-account/${businessUnitUuid}/advertising-settings`);
  }

  updateBusinessUnitAdvertisingSettings(businessUnitUuid: string, settings: {
    use_account_descriptions: boolean;
    advertising_country_descriptions?: CountryDescription[];
  }): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/public-account/${businessUnitUuid}/advertising-settings`, settings);
  }

  updateAdvertisingDefaults(defaults: AdvertisingDefaults): Observable<SingleResult<any>> {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/public-account/advertising-defaults`, defaults)
      .pipe(tap(() => this.cacheApi.clearCached('getAdvertisingAccountSettings')));
  }

  updateBusinessUnitAdvertisingDefaults(businessUnitUuid: string, defaults: AdvertisingDefaults): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/public-account/${businessUnitUuid}/advertising-defaults`, defaults);
  }

  /** BRIEFD-7556 — native portal locations still required for this scope/vehicle. */
  getAdvertisingLocationRequirements(
    params: {businessUnitUuid?: string | null; vehicleUuid?: string | null} = {},
  ): Observable<SingleResult<AdvertisingLocationRequirements>> {
    let httpParams = new HttpParams();
    if (params.businessUnitUuid) {
      httpParams = httpParams.set('businessUnitUuid', params.businessUnitUuid);
    }
    if (params.vehicleUuid) {
      httpParams = httpParams.set('vehicleUuid', params.vehicleUuid);
    }
    return this.http.get<SingleResult<AdvertisingLocationRequirements>>(
      `${environment.apiUrl}/api/public-account/advertising-location-requirements`,
      {params: httpParams},
    );
  }

  switchToNonDemo() {
    this.isDemoBehaviourSubject.next(false);
  }

  sendReactivationEmail(email: string) {
    return this.http.post(`${environment.apiUrl}/api/users/send-reactivation-notification`, {email});
  }

  addUserRole(userId: string, role: string) {
    return this.http.post(`${environment.apiUrl}/api/super-admin/users/add-user-role`, {userId, role});
  }

  getActiveUsers(q: string): Observable<SingleResult<UserProfile[]>> {
    return this.http.post<SingleResult<UserProfile[]>>(`${environment.apiUrl}/api/super-admin/users/active-users`, {q});
  }

  getAccessToken() {
    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/users/access-token`);
  }

  getCompanionQrLoginToken(): Observable<CompanionQrTokenResponse> {
    return this.http.get<CompanionQrTokenResponse>(
      `${environment.apiUrl}/api/auth/companion-qr-token`
    );
  }
}
